import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Code2, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, NPI_SYSTEM } from "@/lib/orgApi";
import { canWriteMainTab } from "@/lib/maintabAccess";
import { ORG } from "@/constants/testIds";
import OrganizationForm from "@/components/organization/OrganizationForm";
import FhirViewer from "@/components/organization/FhirViewer";
import "@/styles/emr-catalog.css";

const npiOf = (r) => (r.identifier || []).find((i) => i.system === NPI_SYSTEM)?.value || "—";
const cityState = (r) => {
  const a = r.address?.[0];
  if (!a) return "—";
  return [a.city, a.state].filter(Boolean).join(", ") || "—";
};

export default function OrganizationsPage() {
  const { user } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "organizations");
  const [orgs, setOrgs] = useState([]);
  const [demoIds, setDemoIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fhirTarget, setFhirTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (name) => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations", {
        params: name ? { name } : {},
      });
      setOrgs(data.items || []);
      setDemoIds(new Set(data.demoIds || []));
    } catch {
      toast.error("Failed to load organizations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/organizations/${deleteTarget.id}`);
      toast.success("Organization deleted.");
      setDeleteTarget(null);
      load(search.trim() || undefined);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid={ORG.page} className="emr-catalog emr-catalog--full-table">
      <div className="emr-catalog__pagehead">
        <div>
          <h1 data-testid={ORG.title} className="emr-catalog__title">
            Organizations
          </h1>
          <p className="emr-catalog__subtitle">
            Practice / business entities — FHIR <span className="font-fhir">Organization</span> resource
          </p>
        </div>
        {canWrite ? (
          <Button
            data-testid={ORG.newBtn}
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="btn emr-catalog__btn"
          >
            <Plus className="emr-catalog__btn-icon" /> New Organization
          </Button>
        ) : null}
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Manage practice entities used across locations, users, and clinical workflows.
            </p>
          </section>
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Search</h2>
            <div className="emr-catalog__filters emr-catalog__filters--search-only">
              <div className="emr-catalog__search">
                <Search className="emr-catalog__search-icon" aria-hidden="true" />
                <input
                  data-testid={ORG.search}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name…"
                  className="emr-catalog__input"
                />
              </div>
            </div>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Organizations</h2>
          {loading ? (
            <div data-testid={ORG.loading} className="emr-catalog__status-msg">Loading…</div>
          ) : orgs.length === 0 ? (
            <div data-testid={ORG.emptyState} className="emr-catalog__empty">
              <Building2 className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No organizations yet. Create your first practice entity.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={ORG.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>NPI</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th className="emr-catalog__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((r) => (
                    <tr key={r.id} data-testid={`${ORG.row}-${r.id}`}>
                      <td className="emr-catalog__name">
                        {r.name}
                        {demoIds.has(r.id) && (
                          <span data-testid={`demo-badge-${r.id}`} className="emr-catalog__demo">DEMO</span>
                        )}
                      </td>
                      <td className="font-fhir emr-catalog__muted">{npiOf(r)}</td>
                      <td className="emr-catalog__muted">{cityState(r)}</td>
                      <td>
                        {r.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Active</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="emr-catalog__row-actions">
                          <Button variant="ghost" size="icon" title="View FHIR" className="emr-catalog-icon-btn"
                            data-testid={`${ORG.viewFhirBtn}-${r.id}`} onClick={() => setFhirTarget(r)}>
                            <Code2 className="h-4 w-4" />
                          </Button>
                          {canWrite ? (
                            <Button variant="ghost" size="icon" title="Edit" className="emr-catalog-icon-btn"
                              data-testid={`${ORG.editBtn}-${r.id}`}
                              onClick={() => { setEditing(r); setFormOpen(true); }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canWrite ? (
                            <Button variant="ghost" size="icon" title="Delete" className="emr-catalog-icon-btn emr-catalog-icon-btn--danger"
                              data-testid={`${ORG.deleteBtn}-${r.id}`} onClick={() => setDeleteTarget(r)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <OrganizationForm open={formOpen} editing={editing}
        onClose={() => setFormOpen(false)} onSaved={() => load(search.trim() || undefined)} />

      <FhirViewer open={!!fhirTarget} resource={fhirTarget} onClose={() => setFhirTarget(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={ORG.deleteDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-medium">{deleteTarget?.name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={ORG.confirmDelete} onClick={confirmDelete}
              className="emr-catalog__btn emr-catalog__btn--danger">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
